// -----------------------------------------------------------------------------
// Client-side dismantling-time tracker.
//
// Lets a mechanic start / pause / resume / stop a stopwatch against a job so
// we can record the actual hands-on dismantling time (separate from
// `job.duration`, which is the *estimated* duration set at creation).
//
// Mirrors the `jobLocks.ts` pattern: state persists in `localStorage`,
// same-tab updates are broadcast through a `CustomEvent`, cross-tab updates
// ride the browser's native `storage` event. Components subscribe via the
// `useDismantlingTimer` hook which also ticks once per second while a session
// is running so the elapsed clock updates live.
//
// A `timer` is an ordered list of sessions. A session is "open" (running)
// when `endedAt === null`; at most one session per job may be open at a
// time. Pausing closes the open session. Resume starts a new one. Stop
// closes the open session and marks the whole timer as stopped so the UI
// can show a "Completed" state until it's explicitly reset.
//
// Backend persistence is pending — see `docs/pending-endpoints.md →
// /job/:id/sessions`. Once that ships, swap `readStore`/`writeStore` for
// a React Query-backed helper without touching any consuming components.
// -----------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const LOCAL_STORAGE_KEY = "wilma.dismantlingTimers.v1";
const CHANGE_EVENT = "wilma:dismantlingTimers:change";

export interface DismantlingSession {
  id: string;
  /** ISO-8601 UTC start timestamp. */
  startedAt: string;
  /** ISO-8601 UTC stop timestamp, or `null` while the session is running. */
  endedAt: string | null;
  /** Wilma user id of the mechanic who started this session, if known. */
  userId: string | null;
}

export interface DismantlingTimer {
  sessions: DismantlingSession[];
  /** Set once the user clicks Stop. Cleared on Reset or a new Start. */
  stoppedAt: string | null;
}

type TimerStore = Record<string, DismantlingTimer>;

export type TimerStatus = "idle" | "running" | "paused" | "stopped";

const EMPTY_TIMER: DismantlingTimer = { sessions: [], stoppedAt: null };

const readStore = (): TimerStore => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeStore = (next: TimerStore) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next));
    // `storage` only fires cross-tab; dispatch our own for same-tab listeners.
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch {
    // Quota / private-mode failures: silently swallow — the timer is a UX
    // aid, not a correctness boundary.
  }
};

const randomId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return (crypto as Crypto).randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const cloneTimer = (t: DismantlingTimer): DismantlingTimer => ({
  sessions: t.sessions.map((s) => ({ ...s })),
  stoppedAt: t.stoppedAt,
});

export function getTimer(jobId: string): DismantlingTimer {
  const store = readStore();
  return store[jobId] ? cloneTimer(store[jobId]) : cloneTimer(EMPTY_TIMER);
}

function saveTimer(jobId: string, timer: DismantlingTimer) {
  const store = readStore();
  const hasSessions = timer.sessions.length > 0;
  const hasStop = !!timer.stoppedAt;
  if (!hasSessions && !hasStop) {
    delete store[jobId];
  } else {
    store[jobId] = timer;
  }
  writeStore(store);
}

const openSession = (timer: DismantlingTimer): DismantlingSession | null =>
  timer.sessions.find((s) => s.endedAt === null) ?? null;

export function getStatus(timer: DismantlingTimer): TimerStatus {
  if (openSession(timer)) return "running";
  if (timer.stoppedAt) return "stopped";
  if (timer.sessions.length > 0) return "paused";
  return "idle";
}

export function getElapsedMs(timer: DismantlingTimer, nowMs: number): number {
  let total = 0;
  for (const s of timer.sessions) {
    const start = Date.parse(s.startedAt);
    const end = s.endedAt ? Date.parse(s.endedAt) : nowMs;
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
      total += end - start;
    }
  }
  return total;
}

/** Starts a fresh session. If a session is already open, this is a no-op. */
export function startSession(jobId: string, userId: string | null) {
  const timer = getTimer(jobId);
  if (openSession(timer)) return;
  timer.sessions.push({
    id: randomId(),
    startedAt: new Date().toISOString(),
    endedAt: null,
    userId,
  });
  // Starting after a stop effectively re-opens the timer — clear the stop
  // so the UI drops out of the "Completed" state.
  timer.stoppedAt = null;
  saveTimer(jobId, timer);
}

/** Closes the currently-open session (no-op if nothing is running). */
export function pauseSession(jobId: string) {
  const timer = getTimer(jobId);
  const open = openSession(timer);
  if (!open) return;
  open.endedAt = new Date().toISOString();
  saveTimer(jobId, timer);
}

/** Closes the open session (if any) and marks the timer as stopped. */
export function stopSession(jobId: string) {
  const timer = getTimer(jobId);
  const open = openSession(timer);
  const now = new Date().toISOString();
  if (open) {
    open.endedAt = now;
  }
  timer.stoppedAt = now;
  saveTimer(jobId, timer);
}

/** Clears all sessions + stop state for this job. */
export function resetTimer(jobId: string) {
  const store = readStore();
  if (store[jobId]) {
    delete store[jobId];
    writeStore(store);
  }
}

export interface UseDismantlingTimerResult {
  timer: DismantlingTimer;
  status: TimerStatus;
  /** Live-updating elapsed milliseconds (ticks every 1s when running). */
  elapsedMs: number;
  start: (userId: string | null) => void;
  pause: () => void;
  resume: (userId: string | null) => void;
  stop: () => void;
  reset: () => void;
}

/**
 * React hook — subscribes to the timer for a given job id. Returns the live
 * elapsed ms plus imperative handlers the UI can bind to buttons. Ticks at
 * 1Hz while a session is running so the clock digits visibly move.
 */
export function useDismantlingTimer(
  jobId: string | null | undefined
): UseDismantlingTimerResult {
  const [timer, setTimer] = useState<DismantlingTimer>(() =>
    jobId ? getTimer(jobId) : cloneTimer(EMPTY_TIMER)
  );
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Re-read the store whenever something changes (our same-tab CustomEvent
  // or another tab's native `storage` event).
  useEffect(() => {
    if (!jobId) return;
    const refresh = () => setTimer(getTimer(jobId));
    refresh();
    window.addEventListener(CHANGE_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [jobId]);

  const status = useMemo(() => getStatus(timer), [timer]);

  // 1Hz tick while running so the clock updates. Pause / stop clears the
  // interval to avoid unnecessary renders.
  useEffect(() => {
    if (status !== "running") {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return;
    }
    setNowMs(Date.now());
    tickRef.current = setInterval(() => setNowMs(Date.now()), 1000);
    return () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [status]);

  const elapsedMs = useMemo(
    () => getElapsedMs(timer, nowMs),
    [timer, nowMs]
  );

  const start = useCallback(
    (userId: string | null) => {
      if (!jobId) return;
      startSession(jobId, userId);
    },
    [jobId]
  );
  const pause = useCallback(() => {
    if (!jobId) return;
    pauseSession(jobId);
  }, [jobId]);
  const resume = useCallback(
    (userId: string | null) => {
      if (!jobId) return;
      startSession(jobId, userId);
    },
    [jobId]
  );
  const stop = useCallback(() => {
    if (!jobId) return;
    stopSession(jobId);
  }, [jobId]);
  const reset = useCallback(() => {
    if (!jobId) return;
    resetTimer(jobId);
  }, [jobId]);

  return { timer, status, elapsedMs, start, pause, resume, stop, reset };
}

/** Formats a ms duration as `HH:MM:SS` (used by the card's big clock). */
export function formatElapsed(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

/** Shorter "2h 14m" style summary, for dense rows (e.g. sessions list). */
export function formatElapsedShort(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  if (totalSec < 60) return `${totalSec}s`;
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}
